import { canUseDOM } from 'exenv';
import _ from 'lodash';
import { FC, useContext, useEffect, useLayoutEffect } from 'react';
import type { CSSProperties } from 'react';
import { withSiteMeta } from '../../HOCS/withSiteMeta';
import { InStreamVideoContext } from './instreamVideoContext';
import { useListCarousel } from '../../scripts/hooks/useListCarousel';
import historySystem from '../../libs/history';
import { scaledAssetUrl } from '../../services/assetResolverService';
import { ConnatixDockedPlayer, useConnatixDockedPlacementRegistration } from './connatixOutstream';
import { PrimisDockedPlayer, usePrimisDockedPlacementRegistration } from './primisOutstream';

function isPrimisVideoProvider(props: { siteConfig?: { videoAdsProvider?: string } }) {
  return props.siteConfig?.videoAdsProvider === 'primis';
}

function isConnatixVideoProvider(props: { siteConfig?: { videoAdsProvider?: string } }) {
  return props.siteConfig?.videoAdsProvider === 'connatix';
}

function shouldSkipGenericVideoUi(props: { siteConfig?: { videoAdsProvider?: string } }) {
  return isPrimisVideoProvider(props) || isConnatixVideoProvider(props);
}

function useHouseVideoUnitProps(props: any) {
  const units = props.siteConfig.houseAds?.video;
  if (!units || units.length === 0) {
    return undefined;
  }

  const unit = useListCarousel<any>(units, { delaySeconds: 60 });

  if (!unit) {
    return undefined;
  }

  return {
    style: {
      cursor: 'pointer',
      backgroundImage: `url(${scaledAssetUrl(unit.imageUrl, {
        width: 904,
        quality: 75
      })})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center center',
      height: '100%'
    },
    onClick: () => {
      if (unit.linkUrl.indexOf('/') === 0) {
        historySystem.push(unit.linkUrl);
      } else {
        window.open(unit.linkUrl, '_blank');
      }
    }
  };
}

const ExpensiveVideoPlayer = withSiteMeta((props: any) => {
  if (shouldSkipGenericVideoUi(props)) {
    return null;
  } else if (props.siteConfig.videoAdsProvider === 'playwire') {
    const videoProps = useHouseVideoUnitProps(props);
    return <div id="pw-video-container" data-width="100%" data-height="100%" {...videoProps}></div>;
  } else if (props.siteConfig.videoAdsProvider === 'venatus') {
    return <div className="vm-placement" id="vm-av" data-format="isvideo"></div>;
  } else if (props.siteConfig.videoAdsProvider === 'anyclip') {
    useLayoutEffect(() => {
      if (canUseDOM && props.visible) {
        const script = document.createElement('script');
        script.src = 'https://player.poptok.com/anyclip-widget/lre-widget/prod/v1/src/lre.js';
        script.setAttribute('pubname', 'raiderio');
        script.setAttribute('widgetname', '0011r000025cE2l_934');
        script.onload = script.remove;
        document.body.appendChild(script);
      }
    }, [props.node, props.visible]);

    return (
      <div id="ac-lre-player-ph">
        <div id="ac-lre-player"></div>
      </div>
    );
  } else if (props.siteConfig.videoAdsProvider === 'exco') {
    useEffect(() => {
      const { ExCoPlayer } = window as any;
      const playerApi = ExCoPlayer ? ExCoPlayer.connect() : null;
      if (playerApi) {
        playerApi.init(
          props.inStreamPlacement
            ? {}
            : {
                sticky: {
                  mode: 'sticky-only',
                  closeButton: true,
                  pauseOnClose: true,
                  width: 300,
                  offset: 20
                }
              }
        );
      }
      return () => {
        if (playerApi) {
          playerApi.destroy();
        }
      };
    }, [props.node]);

    return <div id="1a52ee4b-7ed9-4216-8de0-96524e29a4c3"></div>;
  } else if (props.siteConfig.videoAdsProvider === 'anyclip_outstream') {
    // separate script handles rendering
    return null;
  } else if (props.siteConfig.videoAdsProvider === 'tds') {
    useLayoutEffect(() => {
      if (canUseDOM) {
        const recArgs = {
          containerId: 'pvw-id',
          plId: '11170',
          prId: '11170-142',
          product: 'wpvw'
        };

        if ((window as any).defineRecWidget) {
          (window as any).defineRecWidget(recArgs);
        } else {
          (window as any).recWait = (window as any).recWait || [];
          (window as any).recWait.push(recArgs);
        }
      }
    }, [props.node]);

    return <div id="pvw-id" className="tds-video-container" />;
  } else if (props.siteConfig.videoAdsProvider === 'nitropay') {
    const videoProps = useHouseVideoUnitProps(props);
    return <div id="nitro-video-container" data-width="100%" data-height="100%" {...videoProps}></div>;
  } else {
    return null;
  }
});

export const VideoDesktopComponent = withSiteMeta((props: any) => {
  if (shouldSkipGenericVideoUi(props)) {
    return null;
  }

  return (
    <div style={{ paddingRight: 16, paddingBottom: 5 }}>
      <VideoDockedComponent width={400} height={225} />
    </div>
  );
});

// -----

type TVideoDockedComponentOwnProps = {
  width: number;
  height: number;
  className?: string;
  id?: string;
  style?: string;
  inStreamPlacement: boolean;
  isPlacedAtRoot?: boolean;
  videoAdsProvider: string;
  siteConfig: any;
};

type TVideoDockedComponentStateProps = {};

type TVideoDockedComponentProps = TVideoDockedComponentOwnProps & TVideoDockedComponentStateProps;

function getVideoContainerStyle(
  props: Pick<TVideoDockedComponentProps, 'width' | 'height'>,
  overflow: CSSProperties['overflow'] = 'hidden'
): CSSProperties {
  return {
    overflow,
    position: 'relative',
    width: props.width,
    height: props.height,
    maxWidth: props.width,
    maxHeight: props.height
  };
}

const VideoDockedComponentBase: FC<TVideoDockedComponentProps> = (props) => {
  const { adjustInStreamRefCount } = useContext(InStreamVideoContext);
  const isConnatixProvider = isConnatixVideoProvider(props);
  const isPrimisProvider = isPrimisVideoProvider(props);
  const shouldRegisterInStream = props.inStreamPlacement;
  const shouldRenderConnatixDockedPlacement = isConnatixProvider && props.inStreamPlacement;
  const shouldRenderPrimisDockedPlacement = isPrimisProvider && props.inStreamPlacement;
  useConnatixDockedPlacementRegistration(shouldRenderConnatixDockedPlacement);
  usePrimisDockedPlacementRegistration(shouldRenderPrimisDockedPlacement);

  // component is being removed from the page altogether so mark as no longer instream
  useEffect(() => {
    if (!shouldRegisterInStream) {
      return;
    }

    adjustInStreamRefCount(1);

    return () => {
      adjustInStreamRefCount(-1);
    };
  }, [adjustInStreamRefCount, shouldRegisterInStream]);

  const videoProps = useHouseVideoUnitProps(props);

  if (props.siteConfig.videoAdsProvider === 'tds' && props.inStreamPlacement) {
    // tds will always render from the outstream location, so ignore any instream spots
    return null;
  }

  if (props.siteConfig.videoAdsProvider === 'nitropay' && props.inStreamPlacement && !props.isPlacedAtRoot) {
    // nitro only renders the outstream component
    return null;
  }

  if (props.siteConfig.videoAdsProvider === 'venatus' && !props.inStreamPlacement && !props.isPlacedAtRoot) {
    // venatus will render out stream video from the 1x1, and does not need a dom node for non-instream video
    return null;
  }

  if (shouldRenderPrimisDockedPlacement) {
    return (
      <div
        id={props.id}
        className={props.className}
        data-primis-docked-slot="true"
        style={getVideoContainerStyle(props, 'visible')}
      >
        <PrimisDockedPlayer width={props.width} height={props.height} />
      </div>
    );
  }

  if (shouldRenderConnatixDockedPlacement) {
    return (
      <div
        id={props.id}
        className={props.className}
        data-connatix-docked-slot="true"
        style={getVideoContainerStyle(props)}
      >
        <ConnatixDockedPlayer config={props.siteConfig.connatixConfig} />
      </div>
    );
  }

  if (shouldSkipGenericVideoUi(props)) {
    return null;
  }

  return (
    <div
      id={props.id}
      className={props.className}
      style={getVideoContainerStyle(props)}
    >
      <div {...videoProps}>
        <ExpensiveVideoPlayer type="desktop" {..._.omit(props, 'className', 'id', 'style')} />
      </div>
    </div>
  );
};

export const VideoDockedComponent = withSiteMeta(VideoDockedComponentBase);

const VideoMobileComponentBase: FC<any> = (props) => {
  if (shouldSkipGenericVideoUi(props)) {
    return null;
  }

  return (
    <div className="slds-p-horizontal--x-small">
      <div className="slds-text-align--center" style={{ width: '100%', position: 'relative', margin: '0 auto' }}>
        <ExpensiveVideoPlayer type="mobile" {...props} />
      </div>
    </div>
  );
};

export const VideoMobileComponent = withSiteMeta(VideoMobileComponentBase);
